import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  ViewChild
} from '@angular/core'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { LanguageSwitch } from '../../common/swtich/language.component'
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterLinkActive
} from '@angular/router'
import { Location } from '@angular/common'
import { AuthService, StorageDocument } from '../../../services/auth.service'
import { Locale } from '../../../services/language.service'

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css',
  imports: [TranslatePipe, LanguageSwitch, RouterLink, RouterLinkActive]
})
export class Navbar implements OnInit, AfterViewInit, OnDestroy {
  route = inject(ActivatedRoute)
  router = inject(Router)
  location = inject(Location)
  authService = inject(AuthService)
  tranService = inject(TranslateService)
  private host = inject<ElementRef<HTMLElement>>(ElementRef)
  private navbarResizeObserver: ResizeObserver | null = null
  private lastNavbarHeight = 0
  isShowBackButton = input(false)
  inputTitle = input('')
  preventDefaultGoBack = input(false)
  goBackCallback = output()
  title = ''

  showTermsModal = false
  loadingPdf = false
  siteTncDoc: StorageDocument | null = null

  @ViewChild('tncModalBody') tncModalBody!: ElementRef
  @ViewChild('tncPdfContainer') tncPdfContainer!: ElementRef

  private pdfjsLib: any = null

  get siteTncTitle(): string {
    if (!this.siteTncDoc) return ''
    const lang = this.tranService.currentLang
    if (lang === Locale.ZH_CN) return this.siteTncDoc.name.cn
    if (lang === Locale.ZH_HK) return this.siteTncDoc.name.hk
    return this.siteTncDoc.name.en
  }

  initTitle() {
    if (this.inputTitle()) {
      this.title = this.inputTitle()
      return
    }
    this.route.title.subscribe((title) => {
      this.title = title ? `app.navbar.${title}` : ''
    })
  }

  goBack() {
    this.goBackCallback.emit()
    if (!this.preventDefaultGoBack()) {
      this.defaultGoBack()
    }
  }

  defaultGoBack() {
    this.location.back()
  }

  logout() {
    this.authService.logout().subscribe((success) => {
      if (success) {
        this.router.navigate(['auth', 'login'])
      }
    })
  }

  openTermsModal(): void {
    this.showTermsModal = true
    this.loadingPdf = true
    this.lockBodyScroll()
    if (this.siteTncDoc) {
      this.renderPdf(this.siteTncDoc.url)
    } else {
      this.authService.getSiteTncDoc().subscribe((doc) => {
        this.siteTncDoc = doc
        if (doc?.url) this.renderPdf(doc.url)
        else this.loadingPdf = false
      })
    }
  }

  closeTermsModal(): void {
    this.showTermsModal = false
    this.isScrolledToBottom = false
    this.unlockBodyScroll()
  }

  isScrolledToBottom = false

  onModalScroll(event: Event): void {
    const element = event.target as HTMLElement
    const threshold = 5
    this.isScrolledToBottom = element.scrollHeight - element.scrollTop - element.clientHeight < threshold
  }

  private async renderPdf(url: string): Promise<void> {
    await new Promise(r => setTimeout(r, 150))
    try {
      const containerEl = this.tncPdfContainer?.nativeElement
      const cw = containerEl ? containerEl.clientWidth - 4 : 800
      const pages = await this.renderPdfToImages(url, cw)
      const tc = this.tncPdfContainer?.nativeElement
      if (tc) { tc.innerHTML = ''; pages.forEach(c => tc.appendChild(c)) }
    } catch {
      console.error('PDF render failed')
    }
    this.loadingPdf = false
  }

  private async getPdfJs(): Promise<any> {
    if (this.pdfjsLib) return this.pdfjsLib
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
      script.onload = () => {
        const lib = (window as any).pdfjsLib
        lib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
        this.pdfjsLib = lib
        resolve(lib)
      }
      script.onerror = () => reject(new Error('Failed to load pdf.js'))
      document.head.appendChild(script)
    })
  }

  private async renderPdfToImages(url: string, containerWidth: number): Promise<HTMLImageElement[]> {
    const lib = await this.getPdfJs()
    const pdf = await lib.getDocument(url).promise
    const images: HTMLImageElement[] = []
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i)
      const base = page.getViewport({ scale: 1 })
      const scale = Math.min(1.5, (containerWidth - 4) / base.width)
      const viewport = page.getViewport({ scale })
      const canvas = document.createElement('canvas')
      canvas.width = viewport.width
      canvas.height = viewport.height
      const ctx = canvas.getContext('2d')!
      await page.render({ canvasContext: ctx, viewport }).promise
      const img = document.createElement('img')
      img.className = 'pdf-page'
      img.src = canvas.toDataURL('image/png')
      img.alt = `Page ${i}`
      images.push(img)
    }
    return images
  }

  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.width = '100%'
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.width = ''
  }

  ngOnInit(): void {
    this.initTitle()
  }

  ngAfterViewInit(): void {
    const navbar = this.host.nativeElement
    this.navbarResizeObserver = new ResizeObserver(() => {
      const height = Math.ceil(navbar.getBoundingClientRect().height)
      if (height === this.lastNavbarHeight) return
      this.lastNavbarHeight = height
      document.documentElement.style.setProperty(
        '--navbar-height',
        `${height}px`
      )
    })
    this.navbarResizeObserver.observe(navbar)
  }

  ngOnDestroy(): void {
    this.navbarResizeObserver?.disconnect()
    this.navbarResizeObserver = null
  }
}
